import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["column", "preview", "pool", "form"]

  // Connects the controller and sets up event listeners for drag-and-drop functionality.
  connect() {
    this.columnTargets.forEach(col => this.setupColumnEvents(col))
    this.poolTarget.addEventListener("dragover", this.allowDrop)
    this.poolTarget.addEventListener("drop", this.dropToPool.bind(this))
  }

  // Sets up drag events for an element to make it draggable.
  setupDragEvents(el) {
    el.setAttribute("draggable", "true")
    el.addEventListener("dragstart", (e) => {
      const payload = {
        field: el.dataset.field,
        fieldKey: el.dataset.fieldKey,
        source: this._getParentDropZone(el),
      }
      e.dataTransfer.setData("text/plain", JSON.stringify(payload))
      e.dataTransfer.effectAllowed = "move"
      this.dragged = el
      el.classList.add("dragging")
    })

    el.addEventListener("dragend", () => {
      el.classList.remove("dragging")
    })
  }

  // Sets up drag-and-drop events for a column.
  setupColumnEvents(col) {
    col.addEventListener("dragover", (e) => {
      e.preventDefault()
      const afterElement = this.getDragAfterElement(col, e.clientY)
      if (afterElement == null) {
        col.appendChild(this.dragged)
      } else {
        col.insertBefore(this.dragged, afterElement)
      }
    })
    col.addEventListener("drop", (e) => {
      e.preventDefault()
      this.updatePreview()
    })
  }

  // Determines the element closest to the drag position within a container.
  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll("[data-field]:not(.dragging)")]

    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2
      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      } else {
        return closest
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  // Handles the start of a drag event and sets up the payload.
  dragStart(event) {
    const payload = {
      field: event.target.dataset.field,
      fieldKey: event.target.dataset.fieldKey,
      source: this._getParentDropZone(event.target),
    }
    event.dataTransfer.setData("text/plain", JSON.stringify(payload))
    this.dragged = event.target
  }


  // Allows drop events by preventing the default behavior.
  allowDrop(event) {
    event.preventDefault()
  }

  // Handles dropping an element into a column and updates the preview.
  drop(event) {
    event.preventDefault()
    const payload = JSON.parse(event.dataTransfer.getData("text/plain"))
    const { field: fieldName, fieldKey, source } = payload
    const targetColumn = event.currentTarget

    if (this._fieldExistsInColumn(targetColumn, fieldName)) return

    this._removeFieldFromAllColumns(fieldName)
    const newField = this._buildDraggableField(fieldName, fieldKey)
    targetColumn.appendChild(newField)

    if (source === "pool") {
      this._removeFieldFromPool(fieldName)
    }

    this.updatePreview()
  }

  // Handles dropping an element into the pool and updates the preview.
  dropToPool(event) {
    event.preventDefault()
    const payload = JSON.parse(event.dataTransfer.getData("text/plain"))
    const { field: fieldName, fieldKey } = payload

    if (!this._fieldExistsInPool(fieldName)) {
      const newField = this._buildDraggableField(fieldName, fieldKey)
      this.poolTarget.appendChild(newField)
    }

    this._removeFieldFromAllColumns(fieldName)
    this.updatePreview()
  }


  // Updates the preview section based on the current state of columns and pool.
  updatePreview() {
    // Clears the preview section.
    this.previewTarget.innerHTML = ""

    // Creates a voucher container to hold sections.
    const voucher = document.createElement("div")
    voucher.classList.add("voucher")

    // Adds sections to the voucher for each column.
    this.columnTargets.forEach(() => {
      const section = document.createElement("div")
      section.classList.add("voucher-section")
      voucher.appendChild(section)
    })

    // Populates each section with fields from the corresponding column.
    this.columnTargets.forEach((column, index) => {
      const section = voucher.children[index]
      const fields = Array.from(column.children).filter(el => el.dataset.field)
      fields.forEach(field => {
        const p = document.createElement("p")

        // Creates and appends field label.
        const labelSpan = document.createElement("span")
        labelSpan.classList.add("attr")
        labelSpan.textContent = field.dataset.field

        const br = document.createElement("br")

        // Creates and appends field value
        const valueSpan = document.createElement("span")
        valueSpan.classList.add("value")
        valueSpan.textContent = "value"

        p.appendChild(labelSpan)
        p.appendChild(br)
        p.appendChild(valueSpan)
        section.appendChild(p)
      })
    })

    // Appends the voucher to the preview section.
    this.previewTarget.appendChild(voucher)
  }

  // Prepares and submits the form with the current field configuration.
  submit() {
    // Removes any previously added hidden inputs.
    this.formTarget.querySelectorAll('input[name^="fields"]').forEach(input => input.remove())

    // Adds hidden inputs for fields in columns.
    this.columnTargets.forEach((column, columnIndex) => {
      Array.from(column.children).filter(el => el.dataset.field).forEach((field, position) => {
        this._appendFieldInput(field.dataset.fieldKey, columnIndex, position)
      })
    })

    // Adds hidden inputs for fields in the pool (unassigned).
    Array.from(this.poolTarget.children).filter(el => el.dataset.field).forEach(field => {
      this._appendFieldInput(field.dataset.fieldKey, null, null)
    })
  }

  // Appends hidden input fields to the form for submission.
  _appendFieldInput(fieldKey, column, position) {
    const inputField = (name, value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `fields[][${name}]`
      input.value = value === null ? "" : value
      return input
    }

    this.formTarget.appendChild(inputField("field_key", fieldKey))
    this.formTarget.appendChild(inputField("column", column))
    this.formTarget.appendChild(inputField("position", position))
  }


  // Builds a draggable field element with the given name and key.
  _buildDraggableField(fieldName, fieldKey) {
    const field = document.createElement("div")
    field.classList.add("draggable-field", "mb-2")
    field.setAttribute("draggable", "true")
    field.setAttribute("data-action", "dragstart->field-builder#dragStart")
    field.setAttribute("data-field", fieldName)
    field.setAttribute("data-field-key", fieldKey)
    field.textContent = fieldName
    this.setupDragEvents(field)
    return field
  }

  // Removes a field from all columns.
  _removeFieldFromAllColumns(fieldName) {
    this.columnTargets.forEach(col => {
      Array.from(col.children).forEach(child => {
        if (child.dataset.field === fieldName) child.remove()
      })
    })
  }

  // Removes a field from the pool.
  _removeFieldFromPool(fieldName) {
    Array.from(this.poolTarget.children).forEach(child => {
      if (child.dataset.field === fieldName) child.remove()
    })
  }

  // Checks if a field exists in a specific column.
  _fieldExistsInColumn(column, fieldName) {
    return Array.from(column.children).some(child => child.dataset.field === fieldName)
  }

  // Checks if a field exists in the pool.
  _fieldExistsInPool(fieldName) {
    return Array.from(this.poolTarget.children).some(child => child.dataset.field === fieldName)
  }

  // Determines the parent drop zone of an element.
  _getParentDropZone(el) {
    if (!el || !el.parentElement) return ""
    if (el.parentElement.dataset.fieldBuilderTarget === "pool") return "pool"
    return "column"
  }
}
